// Установка сайта как приложения (PWA). Здесь и только здесь мы разговариваем с
// браузерными API установки; компонент знает лишь состояние — тот же приём, что и
// в lib/push.ts.
// installed — сайт уже открыт как приложение (с домашнего экрана или из ярлыка).
// available — браузер сам сообщил, что установка возможна, и отдал нам своё событие.
// ios_instructions — Safari на iPhone/iPad не умеет beforeinstallprompt и кнопку
// установки не даёт вовсе: единственный путь — «Поделиться» → «На экран „Домой“»,
// поэтому у состояния свой текст без кнопки (см. InstallPrompt.vue).
// manual_instructions — браузер обещал установку, но своего окна установки не
// показывает: остаётся отправить человека в меню браузера. Тоже текст без кнопки.
// external_browser_instructions — сайт открыт во встроенном браузере приложения
// («Яндекс с Алисой» и подобные): установки там нет вовсе, и в меню нет даже пункта
// про домашний экран — только «Открыть во внешнем браузере». Туда и отправляем.
// unavailable — установка сейчас невозможна: браузер не поддерживает установку,
// приложение уже стоит по мнению браузера, либо событие ещё не пришло.
export type InstallState =
  | 'installed'
  | 'available'
  | 'ios_instructions'
  | 'manual_instructions'
  | 'external_browser_instructions'
  | 'unavailable'

// Чем закончился показ окна установки.
// installed — установка подтверждена событием appinstalled, то есть на самом деле;
// dismissed — человек отказался в окне браузера;
// no_dialog — браузер сказал «согласились», но установка так и не случилась: окна он,
// скорее всего, вовсе не показывал (см. waitForInstalled);
// unavailable — показывать было нечего, сохранённого события нет.
export type InstallOutcome = 'installed' | 'dismissed' | 'no_dialog' | 'unavailable'

// Событие браузера приходит почти всегда ПОЗЖЕ монтирования Vue, поэтому о его
// приходе (и об установке) сообщаем окну своим событием: уже смонтированный
// компонент по нему пересчитывает состояние.
export const INSTALLABLE_EVENT = 'pwa:installable'
export const INSTALLED_EVENT = 'pwa:installed'

// Закрытие баннера по умолчанию живёт в sessionStorage, а не в localStorage, намеренно:
// «спрятать до конца сеанса», а не «никогда больше». Человек, закрывший полосу сегодня,
// увидит её в следующий раз — но не будет натыкаться на неё на каждой странице одной вкладки.
// Для тех, кому она мешает совсем, установка всегда доступна блоком в профиле.
// Исключение — iOS: там полоса лишь совет про «Поделиться», кнопки установки нет, и на
// каждый новый сеанс он только раздражает. Крестик на iOS запоминается навсегда
// отдельным ключом в localStorage.
const DISMISSED_KEY = 'pwa_install_dismissed'
const IOS_DISMISSED_KEY = 'pwa_install_ios_dismissed'

// beforeinstallprompt нет в стандартных типах DOM: событие нестандартное, его дают
// только браузеры на Chromium.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferredPrompt: BeforeInstallPromptEvent | null = null

// Установка бывает быстрее, чем браузер разрешает userChoice, и тогда appinstalled
// проходит ДО того, как promptInstall начнёт его ждать. Помним сам факт: событие
// приходит один раз, и пропустить его означало бы объявить установку несостоявшейся.
let installedSeen = false

export function captureInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Без preventDefault() Chromium показывает собственную плашку установки, и наше
    // событие пропадает: показать окно установки позже, по кнопке, станет нечем.
    event.preventDefault()
    deferredPrompt = event as BeforeInstallPromptEvent
    window.dispatchEvent(new Event(INSTALLABLE_EVENT))
  })

  window.addEventListener('appinstalled', () => {
    installedSeen = true
    deferredPrompt = null
    window.dispatchEvent(new Event(INSTALLED_EVENT))
  })
}

function isStandalone(): boolean {
  // navigator.standalone — нестандартное свойство Safari на iOS: там display-mode
  // у web app с домашнего экрана исторически не совпадает с медиазапросом.
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true
}

function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
}

// Да, это осознанное сниффинье user-agent — обычно так делать нельзя, но проверить
// поведение заранее тут нечем. Браузеры Яндекса (Chromium) присылают
// beforeinstallprompt как настоящий Chrome, а их prompt() не показывает никакого
// окна и сразу отвечает «accepted», ничего не установив. Определять это по факту
// (нажали — окна нет) поздно: человек уже потыкал мёртвую кнопку, а это около трети
// российского Android-трафика. Поэтому таким браузерам кнопку не показываем вовсе,
// а сразу объясняем путь через меню.
//
// Проверку пересмотреть примерно через полгода —
// docs/superpowers/backlog/2026-08-29-yabrowser-install-recheck.md.
function isYandexBrowser(): boolean {
  return /YaBrowser/.test(navigator.userAgent)
}

// Встроенный браузер приложения — тот же движок, но окно приложения чужое, и установки
// в нём нет вовсе. «Яндекс с Алисой» подписывается YaSearchBrowser + YaApp_Android
// (старые версии — YandexSearchBrowser/YandexSearch), оболочка Яндекс.Старт —
// YandexStart. Совет про меню браузера ему не годится: в меню нет ни «Установить
// приложение», ни «Добавить на главный экран» — только «Добавить ярлык» (обычный
// ярлык, не приложение) и «Открыть во внешнем браузере». Проверено на живом телефоне
// 2026-08-29, YaApp_Android/26.80.1.
function isEmbeddedBrowser(): boolean {
  return /YaSearchBrowser|YaApp_Android|YandexSearch|YandexStart/.test(navigator.userAgent)
}

export function installState(): InstallState {
  if (isStandalone()) return 'installed'
  if (isIos()) return 'ios_instructions'
  if (deferredPrompt) {
    if (isEmbeddedBrowser()) return 'external_browser_instructions'
    return isYandexBrowser() ? 'manual_instructions' : 'available'
  }
  return 'unavailable'
}

// Сколько ждём appinstalled после «согласия» в окне установки. Настоящая установка
// укладывается в секунду-полторы; пять секунд — запас на медленный телефон, после
// которого молчание браузера уже означает, что установки не было.
const INSTALLED_TIMEOUT_MS = 5000

// Единственное честное доказательство установки — событие appinstalled (второе —
// standalone-режим, но до него надо ещё переоткрыть сайт). Слово браузера
// («accepted» в userChoice) доказательством не является: форки Chromium — тот же
// Яндекс.Браузер, по сообщениям и Samsung Internet — отвечают им, не показав окна и
// ничего не установив.
function waitForInstalled(): Promise<boolean> {
  if (installedSeen) return Promise.resolve(true)

  return new Promise((resolve) => {
    let timer = 0

    const onInstalled = () => {
      window.clearTimeout(timer)
      window.removeEventListener('appinstalled', onInstalled)
      resolve(true)
    }

    timer = window.setTimeout(() => {
      window.removeEventListener('appinstalled', onInstalled)
      resolve(false)
    }, INSTALLED_TIMEOUT_MS)

    window.addEventListener('appinstalled', onInstalled)
  })
}

export async function promptInstall(): Promise<InstallOutcome> {
  const event = deferredPrompt
  if (!event) return 'unavailable'

  // Событие забываем ДО показа окна: браузер запрещает использовать одно и то же
  // beforeinstallprompt дважды, а повторный клик по кнопке, пока окно ещё открыто,
  // иначе привёл бы к молчаливой ошибке. Новое событие браузер пришлёт сам, если
  // установка снова станет возможной.
  deferredPrompt = null

  await event.prompt()
  const { outcome } = await event.userChoice
  if (outcome === 'dismissed') return 'dismissed'

  return await waitForInstalled() ? 'installed' : 'no_dialog'
}

export function dismissBanner(forever = false): void {
  try {
    if (forever) localStorage.setItem(IOS_DISMISSED_KEY, '1')
    else sessionStorage.setItem(DISMISSED_KEY, '1')
  } catch {
    // Приватный режим Safari бросает на запись. Баннер просто останется видимым —
    // ронять из-за этого страницу нельзя.
  }
}

export function bannerDismissed(): boolean {
  try {
    // iOS-ключ прячет полосу в любом состоянии. Это безопасно: пишется он только на
    // iOS, а там installState() кроме installed отдаёт лишь ios_instructions — isIos()
    // стоит сразу после isStandalone(), до проверки события установки.
    return sessionStorage.getItem(DISMISSED_KEY) === '1'
      || localStorage.getItem(IOS_DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}
